import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { X, Download, Smartphone } from 'lucide-react';
import { useSiteConfig } from '../../contexts/SiteConfigContext';

// Custom "Add to Home Screen" prompt. The browser fires
// `beforeinstallprompt` on Chromium when the PWA is installable; we
// stash the event, decide when to show our banner (after ≥2 sessions
// per Google's recommendation), and call `prompt()` only when the
// user clicks our CTA. iOS Safari doesn't fire this event so iOS
// users see a different "Open in Safari → Share → Add to Home" hint.
//
// Gated by the admin-controlled site_config.pwa.install_prompt_enabled
// flag so operators can turn this off entirely if they want a stricter
// install path.

const STORAGE_KEY = 'pwa_install_state';
const SESSION_THRESHOLD = 2; // show banner after this many visits
const DISMISS_COOLDOWN_DAYS = 14; // re-show window after dismissal

const readState = () => {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY)) || {}; }
  catch { return {}; }
};
const writeState = (patch) => {
  const next = { ...readState(), ...patch };
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch {}
  return next;
};

const isStandalone = () =>
  window.matchMedia?.('(display-mode: standalone)').matches ||
  window.navigator?.standalone === true;

const isIos = () => /iphone|ipad|ipod/i.test(navigator.userAgent);

const InstallPwaBanner = () => {
  const { t } = useTranslation('common');
  const { siteConfig } = useSiteConfig();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [showIosHint, setShowIosHint] = useState(false);

  const enabled = siteConfig?.pwa?.install_prompt_enabled !== false
    && siteConfig?.pwa?.enabled !== false;

  // Bump the session counter on mount.
  useEffect(() => {
    if (!enabled || isStandalone()) return;
    const state = writeState({ sessions: (readState().sessions || 0) + 1 });

    const recentlyDismissed = state.dismissedAt
      && (Date.now() - state.dismissedAt) < DISMISS_COOLDOWN_DAYS * 86400000;

    if (recentlyDismissed) return;
    if (state.sessions < SESSION_THRESHOLD) return;

    // For iOS we have no `beforeinstallprompt`; show a hint banner
    // explaining the Share-sheet flow.
    if (isIos() && !state.iosHintShown) {
      setShowIosHint(true);
      writeState({ iosHintShown: true });
    }
  }, [enabled]);

  // Capture the install prompt so we can fire it on user gesture.
  useEffect(() => {
    if (!enabled) return;
    const onPrompt = (e) => {
      e.preventDefault();
      const state = readState();
      if (state.dismissedAt && (Date.now() - state.dismissedAt) < DISMISS_COOLDOWN_DAYS * 86400000) return;
      if ((state.sessions || 0) < SESSION_THRESHOLD) return;
      setDeferredPrompt(e);
      setVisible(true);
    };
    const onInstalled = () => {
      setVisible(false);
      setDeferredPrompt(null);
      writeState({ installedAt: Date.now() });
    };
    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, [enabled]);

  const handleInstall = useCallback(async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const choice = await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    setVisible(false);
    if (choice.outcome === 'dismissed') {
      writeState({ dismissedAt: Date.now() });
    } else {
      writeState({ installedAt: Date.now() });
    }
  }, [deferredPrompt]);

  const handleDismiss = (key) => {
    writeState({ dismissedAt: Date.now() });
    if (key === 'ios') setShowIosHint(false);
    else setVisible(false);
  };

  if (!enabled || isStandalone()) return null;

  // iOS-specific hint (Safari doesn't fire beforeinstallprompt)
  if (showIosHint) {
    return (
      <div className="fixed bottom-4 start-4 end-4 sm:start-auto sm:bottom-6 sm:end-6 sm:w-80 z-[60] bg-white dark:bg-[#1a1a22] rounded-2xl shadow-2xl border border-gray-200 dark:border-white/10 p-4 flex items-start gap-3">
        <span className="inline-flex items-center justify-center w-9 h-9 rounded-lg flex-shrink-0"
              style={{ backgroundColor: 'color-mix(in srgb, var(--color-primary-500) 12%, transparent)' }}>
          <Smartphone className="w-4 h-4" style={{ color: 'var(--color-primary-500)' }} />
        </span>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-gray-900 dark:text-white">{t('pwa.installThisApp', 'Install this app')}</p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            <Trans t={t} i18nKey="pwa.tapShareAddToHome">
              Tap <span className="font-mono">Share</span> → <span className="font-mono">Add to Home Screen</span> to install.
            </Trans>
          </p>
        </div>
        <button onClick={() => handleDismiss('ios')} className="p-1 rounded text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors flex-shrink-0">
          <X className="w-4 h-4" />
        </button>
      </div>
    );
  }

  // Chromium / standard install prompt
  if (!visible || !deferredPrompt) return null;

  return (
    <div className="fixed bottom-4 start-4 end-4 sm:start-auto sm:bottom-6 sm:end-6 sm:w-80 z-[60] bg-white dark:bg-[#1a1a22] rounded-2xl shadow-2xl border border-gray-200 dark:border-white/10 overflow-hidden">
      <div className="p-4">
        <div className="flex items-start gap-3 mb-3">
          <span className="inline-flex items-center justify-center w-9 h-9 rounded-lg flex-shrink-0"
                style={{ backgroundColor: 'color-mix(in srgb, var(--color-primary-500) 12%, transparent)' }}>
            <Download className="w-4 h-4" style={{ color: 'var(--color-primary-500)' }} />
          </span>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-gray-900 dark:text-white">{t('pwa.installForFullExperience', 'Install for the full experience')}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{t('pwa.fasterLoadsHint', 'Faster loads, push notifications, works offline.')}</p>
          </div>
          <button onClick={() => handleDismiss()} className="p-1 rounded text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors flex-shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleInstall}
            className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold text-white transition-colors"
            style={{ backgroundColor: 'var(--color-primary-500)' }}
          >
            <Download className="w-4 h-4" /> {t('pwa.install', 'Install')}
          </button>
          <button
            onClick={() => handleDismiss()}
            className="px-3 py-2 rounded-lg text-sm font-medium text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
          >
            {t('pwa.notNow', 'Not now')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default InstallPwaBanner;
